import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IStationeryStockCountDetailedItem,
  IStationeryStockCountListItem,
} from 'src/types/stationery-stock-count';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  stockCounts: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IStationeryStockCountListItem[];
  };
  stockCount: IStationeryStockCountDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  stockCounts: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  stockCount: null,
};

const stationeryStockCountSlice = createSlice({
  name: 'stationeryStockCount',
  initialState,
  reducers: {
    removeStationeryStockCount: (state, action) => {
      const { stockCountId } = action.payload;

      const index = state.stockCounts.items.findIndex(
        (item) => item._id === stockCountId
      );

      if (index > -1) {
        action.payload.index = index;
        state.stockCounts.items = state.stockCounts.items.filter(
          (item) => item._id !== stockCountId
        );
      }
    },
    rollbackStationeryStockCountDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.stockCounts.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStationeryStockCounts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryStockCounts.fulfilled, (state, action) => {
        state.status = 'success';
        state.stockCounts = action.payload;
      })
      .addCase(getStationeryStockCounts.rejected, (state, action) => {
        state.stockCounts = initialState.stockCounts;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryStockCountById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryStockCountById.fulfilled, (state, action) => {
        state.status = 'success';
        state.stockCount = action.payload;
        state.error = initialState.error;
      })
      .addCase(getStationeryStockCountById.rejected, (state, action) => {
        state.stockCount = initialState.stockCount;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default stationeryStockCountSlice.reducer;

export const {
  removeStationeryStockCount,
  rollbackStationeryStockCountDeletion,
} = stationeryStockCountSlice.actions;

export const getStationeryStockCounts = createAsyncThunk(
  'stationeryStockCount/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `stationery/stock-count?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&startDate=${startDate ? startDate.toISOString() : ''}&endDate=${
          endDate ? endDate.toISOString() : ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryStockCountById = createAsyncThunk(
  'stationeryStockCount/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/stock-count/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteStationeryStockCount = createAsyncThunk(
  'stationeryStockCount/delete',
  async (
    params: { item: IStationeryStockCountListItem; index: number },
    { dispatch }
  ) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`stationery/stock-count/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackStationeryStockCountDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TStationeryStockCountItemInput {
  stationeryItem: string;
  addedQuantity: number;
}

export interface TCreateStationeryStockCount {
  countDate: Date;
  remarks: string | null;
  items: TStationeryStockCountItemInput[];
}

export const createStationeryStockCount = createAsyncThunk(
  'stationeryStockCount/create',
  async (params: { stockCountTransaction: TCreateStationeryStockCount }) => {
    const { stockCountTransaction } = params;
    const response = await axios.post(
      'stationery/stock-count',
      stockCountTransaction
    );
    return response.data.data;
  }
);

export type TUpdateStationeryStockCount = TCreateStationeryStockCount;

export const updateStationeryStockCount = createAsyncThunk(
  'stationeryStockCount/update',
  async (params: {
    stockCountId: string;
    stockCountTransaction: TUpdateStationeryStockCount;
  }) => {
    const { stockCountId, stockCountTransaction } = params;
    const response = await axios.put(
      `stationery/stock-count/${stockCountId}`,
      stockCountTransaction
    );
    return response.data.data;
  }
);
